import { useState, useEffect, useRef } from "react";
import { FileSignature, Upload, CheckCircle2, Lock, FileCheck2, Crown, Clock, ExternalLink, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp", "application/pdf"];
const MAX_BYTES = 5 * 1024 * 1024;

const STEPS = [
  {
    id: "escrito",
    title: "1. Enviar documentos al delegado",
    actorLabel: "Delegante",
    role: "owner",
    description: 'Sube los antecedentes del caso (escritos, exhortos, carpeta), revisa la minuta auto-generada "DELEGA PODER" y luego pulsa "Enviar al delegado". Tu colega recibe el aviso para descargarla y firmarla en pjud.cl con su Clave Única.',
    cta: "Enviar al delegado",
    icon: "send",
    requireFile: false,
  },
  {
    id: "delegado_sign",
    title: "2. Confirmar firma en pjud.cl",
    actorLabel: "Delegado",
    role: "assigned",
    description: "Descarga la minuta DELEGA PODER del bloque superior, ingresa a pjud.cl con tu Clave Única del Registro Civil, firma virtualmente el escrito y vuelve aquí a subir el screenshot del acuse OJV.",
    cta: "Delega poder firmado en pjud ✓",
    callout: "Al confirmar este paso, se libera el cargo Colega Link. El honorario del delegado sigue retenido en Pago Protegido hasta la presentación final del comprobante de la gestión.",
    helpUrl: "https://oficinajudicialvirtual.pjud.cl/",
    requireFile: true,
    icon: "stamp",
  },
  {
    id: "filed",
    title: "3. Entregar Certificado de envío",
    actorLabel: "Delegante",
    role: "owner",
    description: "Sube a pjud.cl la versión final del escrito con ambas firmas y descarga el Certificado de envío del tribunal. Súbelo aquí (con folio si lo conoces) para cerrar esta etapa.",
    cta: "Subir Certificado de envío",
    askFolio: true,
    requireFile: true,
    icon: "file",
  },
];

export default function OJVTracker({ delegationId, isOwner, isAssigned, paymentStatus, onUpdate }) {
  const [status, setStatus] = useState(null);
  const [loadingStep, setLoadingStep] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/delegations/${delegationId}/ojv/status`);
      setStatus(data);
    } catch (err) {
      console.error("OJV status fetch failed:", err);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delegationId]);

  // Guard: only show when escrow paid and there's an assigned lawyer
  if (paymentStatus !== "in_escrow" && paymentStatus !== "released") return null;
  if (!status) return null;

  const stepStates = STEPS.map((s) => ({ ...s, ...status[s.id] }));
  const allDone = stepStates.every((s) => s.completed);

  return (
    <div className="panel p-6 ring-1 ring-amber-400/20 bg-gradient-to-br from-amber-950/10 to-background" data-testid="ojv-tracker">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-400/15 flex items-center justify-center ring-1 ring-amber-400/40 shrink-0">
          <FileSignature size={18} className="text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow text-amber-300">Flujo OJV · Firma electrónica vía Clave Única</div>
          <h3 className="serif text-2xl mt-1 leading-tight">Tracking de la presentación en OJV</h3>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
            Las firmas se realizan dentro de la <a href="https://oficinajudicialvirtual.pjud.cl/" target="_blank" rel="noreferrer" className="text-amber-300 underline inline-flex items-center gap-1">Oficina Judicial Virtual <ExternalLink size={11}/></a>.
            Acá registramos la evidencia de cada paso para liberar el Pago Protegido de forma segura.
          </p>
        </div>
      </div>

      {/* Progress dots */}
      <div className="mt-6 flex items-center gap-2" data-testid="ojv-progress">
        {stepStates.map((s, i) => (
          <Dot key={s.id} step={s} index={i} total={stepStates.length} />
        ))}
      </div>

      {/* Steps */}
      <div className="mt-6 space-y-4">
        {stepStates.map((s, i) => (
          <StepRow
            key={s.id}
            step={s}
            index={i}
            previousDone={i === 0 || stepStates[i - 1].completed}
            canAct={
              !s.completed &&
              (i === 0 || stepStates[i - 1].completed) &&
              ((s.role === "owner" && isOwner) || (s.role === "assigned" && isAssigned))
            }
            loading={loadingStep === s.id}
            error={loadingStep === s.id ? error : ""}
            onUpload={async (file, extra) => {
              setLoadingStep(s.id);
              setError("");
              try {
                const body = {
                  step: s.id,
                  folio: extra?.folio || null,
                  notes: extra?.notes || null,
                };
                if (file) {
                  body.filename = file.name;
                  body.mime = file.type;
                  body.data_b64 = await fileToBase64(file);
                }
                const { data } = await api.post(`/delegations/${delegationId}/ojv/upload`, body);
                if (data.commission_released) {
                  toast.success("Comisión Colega Link liberada ✓ — el honorario sigue retenido para el delegado.");
                } else {
                  toast.success("Paso registrado correctamente.");
                }
                await load();
                if (onUpdate) onUpdate();
              } catch (err) {
                const msg = formatApiError(err.response?.data?.detail) || err.message;
                setError(msg);
                toast.error(msg);
              } finally {
                setLoadingStep(null);
              }
            }}
          />
        ))}
      </div>

      {allDone && (
        <div className="mt-6 p-4 rounded-md bg-emerald-400/10 ring-1 ring-emerald-400/30 flex items-center gap-3">
          <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
          <div className="text-sm">
            <div className="font-medium text-emerald-300">Presentación OJV completa</div>
            <div className="text-xs text-muted-foreground mt-0.5">El delegado puede ejecutar la actuación y subir el comprobante para liberar el honorario.</div>
          </div>
        </div>
      )}

      {status.commission_released && (
        <div className="mt-4 text-[11px] text-amber-300 inline-flex items-center gap-1.5" data-testid="ojv-commission-released">
          <Crown size={11} /> Comisión Colega Link liberada el {new Date(status.commission_released_at).toLocaleString("es-CL")}
        </div>
      )}
    </div>
  );
}

function Dot({ step, index, total }) {
  let cls = "bg-white/10 text-muted-foreground";
  if (step.completed) cls = "bg-emerald-400 text-emerald-950";
  else if (step.locked) cls = "bg-white/5 text-muted-foreground";
  return (
    <>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold transition-colors ${cls}`}>
        {step.completed ? <CheckCircle2 size={14}/> : (index + 1)}
      </div>
      {index < total - 1 && (
        <div className={`h-px flex-1 ${step.completed ? "bg-emerald-400/60" : "bg-white/10"}`} />
      )}
    </>
  );
}

function StepRow({ step, index, previousDone, canAct, loading, error, onUpload }) {
  const fileRef = useRef(null);
  const [folio, setFolio] = useState("");
  const [notes, setNotes] = useState("");
  const [pickedName, setPickedName] = useState("");

  const isLocked = !previousDone && !step.completed;

  const handlePick = () => fileRef.current?.click();
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      toast.error("Formato no permitido (JPG / PNG / WEBP / PDF)");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("Archivo demasiado grande (máx 5 MB)");
      return;
    }
    setPickedName(file.name);
    await onUpload(file, { folio: folio || null, notes: notes || null });
    e.target.value = "";
  };

  let stateBadge;
  if (step.completed) {
    stateBadge = <span className="pill badge-verified">Completado ✓</span>;
  } else if (isLocked) {
    stateBadge = <span className="pill"><Lock size={10} className="inline mr-1"/> Bloqueado</span>;
  } else if (canAct) {
    stateBadge = <span className="pill badge-cyan">Tu turno</span>;
  } else {
    stateBadge = <span className="pill"><Clock size={10} className="inline mr-1"/> Esperando {step.actorLabel.toLowerCase()}</span>;
  }

  return (
    <div className={`panel p-5 ${step.completed ? "ring-1 ring-emerald-400/30" : canAct ? "ring-1 ring-amber-400/40" : ""}`} data-testid={`ojv-step-${step.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold tracking-wider text-muted-foreground">PASO {index + 1} · {step.actorLabel.toUpperCase()}</div>
          <div className="font-medium mt-1">{step.title}</div>
        </div>
        {stateBadge}
      </div>

      <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{step.description}</p>

      {step.callout && !step.completed && (
        <div className="mt-3 p-3 rounded-md bg-amber-400/5 ring-1 ring-amber-400/30 text-xs text-amber-200/90 inline-flex items-start gap-2">
          <AlertTriangle size={12} className="text-amber-400 mt-0.5 shrink-0"/>
          <span>{step.callout}</span>
        </div>
      )}

      {step.completed && step.file && (
        <div className="mt-3 text-xs text-muted-foreground inline-flex items-center gap-2">
          <FileCheck2 size={12} className="text-emerald-400"/>
          <span>{step.file.filename}</span>
          {step.folio && <span className="pill text-[10px]">Folio {step.folio}</span>}
          <span>· {new Date(step.at).toLocaleString("es-CL")}</span>
        </div>
      )}

      {canAct && !step.completed && (
        <div className="mt-4 space-y-3">
          {step.askFolio && (
            <input
              className="field text-sm"
              placeholder="N° folio del tribunal (opcional pero recomendado)"
              value={folio}
              onChange={(e) => setFolio(e.target.value)}
              data-testid={`ojv-folio-${step.id}`}
            />
          )}
          {step.requireFile && (
            <>
              <textarea
                className="field text-sm"
                placeholder="Notas internas (opcional, solo visibles para vosotros)"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid={`ojv-notes-${step.id}`}
              />
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                onChange={handleFile}
                className="hidden"
                data-testid={`ojv-input-${step.id}`}
              />
              <button
                type="button"
                onClick={handlePick}
                disabled={loading}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                data-testid={`ojv-upload-${step.id}`}
              >
                <Upload size={14} /> {loading ? "Subiendo…" : step.cta}
              </button>
              {pickedName && !loading && <div className="text-[11px] text-muted-foreground">{pickedName}</div>}
            </>
          )}
          {!step.requireFile && (
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm("¿Confirmas que los documentos están listos? Se notificará al delegado para que firme en pjud.cl.")) return;
                await onUpload(null, {});
              }}
              disabled={loading}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
              data-testid={`ojv-send-${step.id}`}
            >
              <CheckCircle2 size={14} /> {loading ? "Enviando…" : step.cta}
            </button>
          )}
          {error && <div className="text-xs text-red-400" data-testid={`ojv-error-${step.id}`}>{error}</div>}
          {step.helpUrl && (
            <a href={step.helpUrl} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              Abrir pjud.cl <ExternalLink size={10}/>
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
    reader.readAsDataURL(file);
  });
}
